
import { Course } from './types';

export const REACT_PERFORMANCE_COURSE: Course = {
  id: "react-performance",
  title: "React Performance Optimization",
  description: "Master advanced performance optimization techniques for React applications",
  level: "advanced",
  duration: "8 hours",
  totalDuration: "8 hours",
  estimatedHours: 8,
  badge: "Performance Expert",
  modules: [
    {
      id: "perf-1",
      title: "Performance Fundamentals",
      description: "Understanding React performance bottlenecks and measurement techniques",
      duration: "90 minutes",
      estimatedTime: 90,
      difficulty: 'advanced',
      prerequisites: ['advanced-3'],
      learningObjectives: [
        "Identify performance bottlenecks",
        "Use React DevTools Profiler",
        "Understand rendering behavior",
        "Measure performance metrics"
      ],
      content: `
        <h1>Performance Fundamentals</h1>
        
        <h2>React Performance Bottlenecks</h2>
        <ul>
          <li><strong>Unnecessary Re-renders:</strong> Components updating when they shouldn't</li>
          <li><strong>Expensive Calculations:</strong> Heavy computations on every render</li>
          <li><strong>Large Bundle Sizes:</strong> Slow initial load times</li>
          <li><strong>Memory Leaks:</strong> Accumulating memory usage over time</li>
          <li><strong>Inefficient List Rendering:</strong> Poor key usage and virtualization</li>
        </ul>
        
        <h2>React DevTools Profiler</h2>
        <p>The Profiler helps identify performance issues:</p>
        
        <pre><code>// Enable profiler in development
import { Profiler } from 'react';

function onRenderCallback(id, phase, actualDuration, baseDuration, startTime, commitTime, interactions) {
  console.log('Component:', id);
  console.log('Phase:', phase); // 'mount' or 'update'
  console.log('Actual duration:', actualDuration);
  console.log('Base duration:', baseDuration);
}

function App() {
  return (
    <Profiler id="App" onRender={onRenderCallback}>
      <MyComponent />
    </Profiler>
  );
}</code></pre>
        
        <h2>Measuring Performance</h2>
        <pre><code>// Performance measurement hooks
import { useState, useEffect } from 'react';

function usePerformanceTimer(name) {
  useEffect(() => {
    performance.mark(\`\${name}-start\`);
    
    return () => {
      performance.mark(\`\${name}-end\`);
      performance.measure(name, \`\${name}-start\`, \`\${name}-end\`);
      
      const measure = performance.getEntriesByName(name)[0];
      console.log(\`\${name} took \${measure.duration}ms\`);
    };
  }, [name]);
}

// Usage
function ExpensiveComponent() {
  usePerformanceTimer('ExpensiveComponent');
  
  // Component logic
  return <div>Expensive component</div>;
}

// Web Vitals measurement
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

getCLS(console.log);
getFID(console.log);
getFCP(console.log);
getLCP(console.log);
getTTFB(console.log);</code></pre>
        
        <h2>React Rendering Behavior</h2>
        <pre><code>// Understanding when components re-render
function Parent() {
  const [count, setCount] = useState(0);
  const [name, setName] = useState('');
  
  console.log('Parent renders');
  
  return (
    <div>
      <button onClick={() => setCount(count + 1)}>
        Count: {count}
      </button>
      <input 
        value={name} 
        onChange={(e) => setName(e.target.value)} 
        placeholder="Name"
      />
      {/* Child re-renders when Parent re-renders, even if props don't change */}
      <Child message="Hello" />
    </div>
  );
}

function Child({ message }) {
  console.log('Child renders');
  return <div>{message}</div>;
}

// Solution: React.memo
const OptimizedChild = React.memo(function Child({ message }) {
  console.log('OptimizedChild renders');
  return <div>{message}</div>;
});</code></pre>
        
        <h2>Bundle Analysis</h2>
        <pre><code>// Analyze bundle size
npm install --save-dev webpack-bundle-analyzer

// Add to package.json
{
  "scripts": {
    "analyze": "npm run build && npx webpack-bundle-analyzer build/static/js/*.js"
  }
}

// Or use source-map-explorer
npm install --save-dev source-map-explorer

{
  "scripts": {
    "analyze": "npm run build && npx source-map-explorer 'build/static/js/*.js'"
  }
}</code></pre>
      `,
      practice: {
        task: "Identify and fix performance issues in a component using React DevTools",
        starter: `// Analyze this component for performance issues
const ProductList = ({ products, category, onProductClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredProducts = products.filter(product => {
    return product.category === category && 
           product.name.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  const expensiveCalculation = () => {
    let result = 0;
    for (let i = 0; i < 1000000; i++) {
      result += Math.random();
    }
    return result;
  };
  
  return (
    <div>
      <input 
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search products..."
      />
      <div>Expensive result: {expensiveCalculation()}</div>
      {filteredProducts.map(product => (
        <ProductCard 
          key={product.id}
          product={product}
          onClick={() => onProductClick(product)}
        />
      ))}
    </div>
  );
};`,
        solution: `import React, { useState, useMemo, useCallback } from 'react';

const ProductList = ({ products, category, onProductClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Memoize filtered products to avoid recalculation on every render
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      return product.category === category && 
             product.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [products, category, searchTerm]);
  
  // Memoize expensive calculation
  const expensiveResult = useMemo(() => {
    let result = 0;
    for (let i = 0; i < 1000000; i++) {
      result += Math.random();
    }
    return result;
  }, []); // Only calculate once
  
  // Memoize click handler to prevent unnecessary re-renders of ProductCard
  const handleProductClick = useCallback((product) => {
    onProductClick(product);
  }, [onProductClick]);
  
  return (
    <div>
      <input 
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search products..."
      />
      <div>Expensive result: {expensiveResult}</div>
      {filteredProducts.map(product => (
        <MemoizedProductCard 
          key={product.id}
          product={product}
          onClick={handleProductClick}
        />
      ))}
    </div>
  );
};

// Memoize ProductCard to prevent unnecessary re-renders
const MemoizedProductCard = React.memo(ProductCard);

export default ProductList;`,
        hints: [
          "Use useMemo for expensive calculations and filtered data",
          "Use useCallback for event handlers passed as props",
          "Memoize child components with React.memo",
          "Move expensive calculations outside render or memoize them"
        ]
      }
    },
    {
      id: "perf-2",
      title: "Memoization Techniques",
      description: "Master React.memo, useMemo, and useCallback for optimal performance",
      duration: "100 minutes",
      estimatedTime: 100,
      difficulty: 'advanced',
      prerequisites: ['perf-1'],
      learningObjectives: [
        "Master React.memo usage patterns",
        "Optimize with useMemo and useCallback",
        "Understand memoization trade-offs",
        "Implement custom memoization"
      ],
      content: `
        <h1>Memoization Techniques</h1>
        
        <h2>React.memo Deep Dive</h2>
        <pre><code>// Basic React.memo usage
const ExpensiveComponent = React.memo(function ExpensiveComponent({ data, onAction }) {
  console.log('ExpensiveComponent renders');
  
  return (
    <div>
      {data.map(item => (
        <div key={item.id}>{item.name}</div>
      ))}
    </div>
  );
});

// Custom comparison function
const OptimizedComponent = React.memo(function OptimizedComponent({ user, settings }) {
  return <div>{user.name} - {settings.theme}</div>;
}, (prevProps, nextProps) => {
  // Only re-render if user.name or settings.theme changed
  return prevProps.user.name === nextProps.user.name &&
         prevProps.settings.theme === nextProps.settings.theme;
});

// Memoizing with complex props
const DeepComparisonComponent = React.memo(function DeepComparisonComponent({ config }) {
  return <div>{JSON.stringify(config)}</div>;
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config);
});</code></pre>
        
        <h2>useMemo Patterns</h2>
        <pre><code>import { useMemo } from 'react';

function DataVisualization({ data, filters, sortBy }) {
  // Expensive filtering and sorting
  const processedData = useMemo(() => {
    console.log('Processing data...');
    
    let filtered = data.filter(item => {
      return filters.every(filter => filter.fn(item));
    });
    
    let sorted = filtered.sort((a, b) => {
      if (sortBy.direction === 'asc') {
        return a[sortBy.field] > b[sortBy.field] ? 1 : -1;
      } else {
        return a[sortBy.field] < b[sortBy.field] ? 1 : -1;
      }
    });
    
    return sorted;
  }, [data, filters, sortBy]);
  
  // Expensive calculations
  const statistics = useMemo(() => {
    console.log('Calculating statistics...');
    
    return {
      total: processedData.length,
      average: processedData.reduce((sum, item) => sum + item.value, 0) / processedData.length,
      max: Math.max(...processedData.map(item => item.value)),
      min: Math.min(...processedData.map(item => item.value))
    };
  }, [processedData]);
  
  // Memoize expensive renders
  const chartComponent = useMemo(() => {
    console.log('Rendering chart...');
    return <ExpensiveChart data={processedData} />;
  }, [processedData]);
  
  return (
    <div>
      <div>Total: {statistics.total}</div>
      <div>Average: {statistics.average}</div>
      {chartComponent}
    </div>
  );
}</code></pre>
        
        <h2>useCallback Optimization</h2>
        <pre><code>import { useCallback, useState } from 'react';

function TodoApp() {
  const [todos, setTodos] = useState([]);
  const [filter, setFilter] = useState('all');
  
  // Stable function references
  const addTodo = useCallback((text) => {
    setTodos(prev => [...prev, { id: Date.now(), text, completed: false }]);
  }, []); // No dependencies, stable reference
  
  const toggleTodo = useCallback((id) => {
    setTodos(prev => prev.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  }, []); // No dependencies needed
  
  const deleteTodo = useCallback((id) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  }, []);
  
  // Callback with dependencies
  const filteredTodos = useMemo(() => {
    switch (filter) {
      case 'active':
        return todos.filter(todo => !todo.completed);
      case 'completed':
        return todos.filter(todo => todo.completed);
      default:
        return todos;
    }
  }, [todos, filter]);
  
  return (
    <div>
      <TodoInput onAdd={addTodo} />
      <FilterButtons currentFilter={filter} onFilterChange={setFilter} />
      <TodoList 
        todos={filteredTodos} 
        onToggle={toggleTodo}
        onDelete={deleteTodo}
      />
    </div>
  );
}

// Memoized components receive stable callbacks
const TodoList = React.memo(function TodoList({ todos, onToggle, onDelete }) {
  console.log('TodoList renders');
  
  return (
    <ul>
      {todos.map(todo => (
        <TodoItem
          key={todo.id}
          todo={todo}
          onToggle={onToggle}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
});

const TodoItem = React.memo(function TodoItem({ todo, onToggle, onDelete }) {
  console.log(\`TodoItem \${todo.id} renders\`);
  
  return (
    <li>
      <input
        type="checkbox"
        checked={todo.completed}
        onChange={() => onToggle(todo.id)}
      />
      <span>{todo.text}</span>
      <button onClick={() => onDelete(todo.id)}>Delete</button>
    </li>
  );
});</code></pre>
        
        <h2>Custom Memoization Hooks</h2>
        <pre><code>// Custom hook for deep comparison memoization
import { useRef, useEffect } from 'react';

function useDeepMemo(fn, deps) {
  const ref = useRef();
  
  if (!ref.current || !deepEqual(deps, ref.current.deps)) {
    ref.current = {
      deps,
      value: fn()
    };
  }
  
  return ref.current.value;
}

function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// Memoize async operations
function useAsyncMemo(asyncFn, deps) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  
  useEffect(() => {
    let cancelled = false;
    
    setState({ loading: true, data: null, error: null });
    
    asyncFn()
      .then(data => {
        if (!cancelled) {
          setState({ loading: false, data, error: null });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({ loading: false, data: null, error });
        }
      });
    
    return () => {
      cancelled = true;
    };
  }, deps);
  
  return state;
}

// Usage
function UserProfile({ userId }) {
  const userState = useAsyncMemo(
    () => fetchUser(userId),
    [userId]
  );
  
  if (userState.loading) return <div>Loading...</div>;
  if (userState.error) return <div>Error: {userState.error.message}</div>;
  
  return <div>Welcome, {userState.data.name}!</div>;
}</code></pre>
        
        <h2>Memoization Anti-patterns</h2>
        <pre><code>// ❌ Don't memoize everything
const OverMemoizedComponent = React.memo(function Simple({ text }) {
  return <div>{text}</div>; // Simple component, memoization overhead > benefit
});

// ❌ Memoizing with always-changing dependencies
function BadMemoization({ items }) {
  const processedItems = useMemo(() => {
    return items.map(item => ({ ...item, timestamp: Date.now() }));
  }, [items]); // timestamp always changes, memo is useless
  
  return <div>{processedItems.length}</div>;
}

// ❌ Incorrect dependency arrays
function IncorrectDeps({ data, config }) {
  const result = useMemo(() => {
    return processData(data, config.settings);
  }, [data]); // Missing config.settings dependency
  
  return <div>{result}</div>;
}

// ✅ Correct patterns
const EfficientComponent = React.memo(function Efficient({ data, onAction }) {
  const processedData = useMemo(() => {
    return expensiveOperation(data);
  }, [data]);
  
  const handleAction = useCallback((item) => {
    onAction(item);
  }, [onAction]);
  
  return (
    <div>
      {processedData.map(item => (
        <Item key={item.id} item={item} onAction={handleAction} />
      ))}
    </div>
  );
}, (prev, next) => {
  return prev.data === next.data && prev.onAction === next.onAction;
});</code></pre>
      `,
      practice: {
        task: "Optimize a complex data table component with sorting, filtering, and pagination",
        starter: `// Optimize this data table for performance
const DataTable = ({ data, columns, pageSize = 10 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };
  
  const handleFilter = (column, value) => {
    setFilters({ ...filters, [column]: value });
  };
  
  // Process data
  let processedData = data.filter(row => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return row[key].toString().toLowerCase().includes(filters[key].toLowerCase());
    });
  });
  
  if (sortConfig.key) {
    processedData.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  
  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = processedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  
  return (
    <div>
      <div>
        {columns.map(column => (
          <input
            key={column.key}
            placeholder={\`Filter \${column.label}\`}
            onChange={(e) => handleFilter(column.key, e.target.value)}
          />
        ))}
      </div>
      
      <table>
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column.key} onClick={() => handleSort(column.key)}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map(row => (
            <TableRow key={row.id} row={row} columns={columns} />
          ))}
        </tbody>
      </table>
      
      <div>
        <button 
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button 
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};`,
        solution: `import React, { useState, useMemo, useCallback } from 'react';

const DataTable = React.memo(function DataTable({ data, columns, pageSize = 10 }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  
  // Memoize sort handler
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  }, []);
  
  // Memoize filter handler
  const handleFilter = useCallback((column, value) => {
    setFilters(prev => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  }, []);
  
  // Memoize filtered data
  const filteredData = useMemo(() => {
    return data.filter(row => {
      return Object.keys(filters).every(key => {
        if (!filters[key]) return true;
        return row[key].toString().toLowerCase().includes(filters[key].toLowerCase());
      });
    });
  }, [data, filters]);
  
  // Memoize sorted data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    const sorted = [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  }, [filteredData, sortConfig]);
  
  // Memoize pagination calculations
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(sortedData.length / pageSize);
    const paginatedData = sortedData.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
    
    return { totalPages, paginatedData };
  }, [sortedData, currentPage, pageSize]);
  
  // Memoize navigation handlers
  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);
  
  const goToNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(paginationData.totalPages, prev + 1));
  }, [paginationData.totalPages]);
  
  return (
    <div>
      <FilterRow columns={columns} onFilter={handleFilter} />
      
      <table>
        <TableHeader 
          columns={columns} 
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <TableBody 
          data={paginationData.paginatedData}
          columns={columns}
        />
      </table>
      
      <Pagination
        currentPage={currentPage}
        totalPages={paginationData.totalPages}
        onPrevious={goToPreviousPage}
        onNext={goToNextPage}
      />
    </div>
  );
});

// Memoized sub-components
const FilterRow = React.memo(function FilterRow({ columns, onFilter }) {
  return (
    <div>
      {columns.map(column => (
        <FilterInput
          key={column.key}
          column={column}
          onFilter={onFilter}
        />
      ))}
    </div>
  );
});

const FilterInput = React.memo(function FilterInput({ column, onFilter }) {
  const handleChange = useCallback((e) => {
    onFilter(column.key, e.target.value);
  }, [column.key, onFilter]);
  
  return (
    <input
      placeholder={\`Filter \${column.label}\`}
      onChange={handleChange}
    />
  );
});

const TableHeader = React.memo(function TableHeader({ columns, sortConfig, onSort }) {
  return (
    <thead>
      <tr>
        {columns.map(column => (
          <SortableHeader
            key={column.key}
            column={column}
            sortConfig={sortConfig}
            onSort={onSort}
          />
        ))}
      </tr>
    </thead>
  );
});

const SortableHeader = React.memo(function SortableHeader({ column, sortConfig, onSort }) {
  const handleClick = useCallback(() => {
    onSort(column.key);
  }, [column.key, onSort]);
  
  const getSortIcon = () => {
    if (sortConfig.key !== column.key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };
  
  return (
    <th onClick={handleClick} style={{ cursor: 'pointer' }}>
      {column.label}{getSortIcon()}
    </th>
  );
});

const TableBody = React.memo(function TableBody({ data, columns }) {
  return (
    <tbody>
      {data.map(row => (
        <TableRow key={row.id} row={row} columns={columns} />
      ))}
    </tbody>
  );
});

const TableRow = React.memo(function TableRow({ row, columns }) {
  return (
    <tr>
      {columns.map(column => (
        <td key={column.key}>
          {row[column.key]}
        </td>
      ))}
    </tr>
  );
});

const Pagination = React.memo(function Pagination({ 
  currentPage, 
  totalPages, 
  onPrevious, 
  onNext 
}) {
  return (
    <div>
      <button 
        disabled={currentPage === 1}
        onClick={onPrevious}
      >
        Previous
      </button>
      <span>Page {currentPage} of {totalPages}</span>
      <button 
        disabled={currentPage === totalPages}
        onClick={onNext}
      >
        Next
      </button>
    </div>
  );
});

export default DataTable;`,
        hints: [
          "Separate filtering, sorting, and pagination into different useMemo hooks",
          "Memoize all callback functions with useCallback",
          "Break down into smaller memoized components",
          "Reset pagination when filters or sorting change"
        ]
      }
    }
  ],
  requirements: [
    "Advanced React knowledge including hooks",
    "Understanding of JavaScript performance concepts",
    "Familiarity with React DevTools",
    "Experience with complex React applications"
  ],
  tags: ["Performance", "React", "Optimization", "Memoization", "Advanced"],
  imageSrc: "/images/course-thumbnail.png",
  learningOutcomes: [
    "Identify and resolve React performance bottlenecks",
    "Master memoization techniques and patterns",
    "Optimize large-scale React applications",
    "Use profiling tools effectively",
    "Implement performance monitoring",
    "Balance performance vs code complexity"
  ],
  certificateTemplate: "react-performance-optimization"
};
